/**
 * Breakout manager panel - the host/presenter surface for breakout rooms
 * (docs/breakout-rooms.md). Pure DOM: no Babylon, no Photon. Network intent
 * goes through the handlers supplied by multiplayer.js via
 * registerBreakoutHandlers (the registerPresenterHandlers pattern); state
 * arrives through roomStore.
 *
 * Deliberately a SEPARATE element from the Connected Users panel: that panel
 * is a wholesale innerHTML rebuild on every roster change, which would wipe
 * focus and value from the text inputs here (room names, broadcast composer).
 * This panel is built with DOM nodes, keeps its shell stable, and repaints
 * the live sections in place - never while one of its inputs has focus.
 *
 * Two views:
 *  - Setup (no active session): room count + names, mode picker, optional
 *    timer, manual-assignment grid, "Open rooms".
 *  - Live (session active): per-room cards with live occupancy and per-person
 *    "Move to" controls, Visit buttons, broadcast composer, timer controls,
 *    Close rooms with a cancellable grace countdown.
 *
 * All user-controlled text (names, messages) renders via textContent.
 */

import { CONFIG } from '../../config/client.config.js';
import { can } from '../../config/roles.js';
import { TOKENS, createIcon } from '../designSystem.js';
import { setPanelOpen, uiPanelsStore } from '../../state/uiPanelsStore';
import { modalManager } from '../ModalManager.js';
import { showToast } from '../../utils/ui.js';
import { STANDARD_BREAKOUT_TEMPLATE_ID } from '../../utils/breakoutProtocol';
import { defaultBreakoutTemplateId } from '../../services/breakoutTemplates.js';
import { roomStore } from '../../state/roomStore';
import { X, Plus, Minus, Megaphone, DoorOpen, Footprints, TimerReset, Users } from 'lucide';
import { BreakoutPanelOwner } from './breakoutPanelOwner';
import {
  breakoutLiveSignatures,
  formatBreakoutCountdown as formatCountdown,
} from './breakoutViewModels';
import { BreakoutDraftOwner } from './breakoutDraftOwner';

const BREAKOUT = CONFIG.BREAKOUT;

let handlers = null; // supplied by multiplayer.js
let panel = null;
let overlay = null;
let bodyEl = null;
let owner = null;
let subscriptionsInstalled = false;
let view = null; // 'setup' | 'live'
/** @type {import('../../utils/breakoutProtocol').BreakoutState|{active: false}|null} */
let state = { active: false };

// Setup-form state survives close/reopen but resets at the terminal session boundary.
const drafts = new BreakoutDraftOwner();
let breakoutTemplates = [{ id: STANDARD_BREAKOUT_TEMPLATE_ID, name: 'Standard Lounge' }];
// Platform-flagged default template; pre-selects each room until the host
// picks something. Standard Lounge until the catalog reports otherwise.
let catalogDefaultId = STANDARD_BREAKOUT_TEMPLATE_ID;
let templateLoadPromise = null;
let templateCatalogStatus = 'idle';

/**
 * @param {Object} h - handlers + providers from multiplayer.js
 * @param {() => Promise<Array<{id: string, name: string}>>} h.getTemplates
 * @param {(opts: {mode: string, rooms: Array<{name: string, templateId: string,
 *   templateName: string}>, minutes: number|null, assignments: Record<string, string>}) => Promise<any>} h.onOpen
 * @param {(targetActorNr: number, roomId: string|null) => Promise<void>} h.onAssign
 * @param {(roomId: string, name: string) => Promise<void>} h.onRename
 * @param {(minutes: number|null) => Promise<void>} h.onSetTimer
 * @param {(minutes: number) => Promise<void>} h.onExtendTimer
 * @param {(roomId: string) => Promise<boolean>} h.onVisit
 * @param {() => Promise<boolean>} h.onReturnToMain
 * @param {(text: string) => Promise<void>} h.onBroadcast
 * @param {() => Promise<void>} h.onCloseStart
 * @param {() => Promise<void>} h.onCloseCancel
 */
export function registerBreakoutHandlers(h) {
  if (!owner) owner = new BreakoutPanelOwner();
  handlers = h;
  owner.replaceHandlers();
  templateLoadPromise = null;
  ensureSubscriptions();
  if (uiPanelsStore.getState().open.breakouts) owner.startTick(onTick);
}

function ensureSubscriptions() {
  if (!owner) return;
  if (subscriptionsInstalled) return;
  subscriptionsInstalled = true;
  owner.addCleanup(
    roomStore.subscribe(
      (s) => s.breakout,
      (next) => {
        state = next || { active: false };
        if (isPanelOpen()) renderForState();
      },
      { fireImmediately: true },
    ),
  );
  owner.addCleanup(
    roomStore.subscribe(
      (s) => s.roster,
      () => {
        if (isPanelOpen() && view === 'live') repaintLive();
      },
    ),
  );
  owner.addCleanup(
    uiPanelsStore.subscribe(
      (s) => s.open.breakouts,
      (visible) => {
        if (visible) {
          if (!handlers) return;
          state = roomStore.getState().breakout || { active: false };
          ensurePanel();
          overlay.style.display = 'flex';
          view = null;
          renderForState();
          if (!state.active) loadTemplateCatalog();
          owner?.startTick(onTick);
        } else {
          if (overlay) overlay.style.display = 'none';
          owner?.stopTick();
        }
      },
      { fireImmediately: true },
    ),
  );
}

/** Host action bar button hook (multiplayerUI.js). */
export function toggleBreakoutPanel() {
  if (isPanelOpen()) closePanel();
  else openPanel();
}

function isPanelOpen() {
  return uiPanelsStore.getState().open.breakouts;
}

function openPanel() {
  if (!handlers) return;
  ensureSubscriptions();
  state = roomStore.getState().breakout || { active: false };
  ensurePanel();
  setPanelOpen('breakouts', true);
  overlay.style.display = 'flex';
  document.exitPointerLock?.();
  view = null; // force a fresh view build
  renderForState();
  if (!state?.active) loadTemplateCatalog();
  owner?.startTick(onTick);
}

function loadTemplateCatalog() {
  if (templateLoadPromise || !handlers?.getTemplates) return templateLoadPromise;
  const operation = owner?.captureOperation();
  const catalogGeneration = owner?.beginCatalogLoad();
  const getTemplates = handlers.getTemplates;
  if (!operation || catalogGeneration === undefined) return null;
  templateCatalogStatus = 'loading';
  templateLoadPromise = getTemplates()
    .then((items) => {
      if (!owner?.isCatalogCurrent(operation, catalogGeneration)) return;
      const deduped = new Map();
      for (const item of items || []) {
        if (typeof item?.id === 'string' && typeof item?.name === 'string') {
          deduped.set(item.id, {
            id: item.id,
            name: item.name,
            isDefault: item.isDefault === true,
          });
        }
      }
      if (!deduped.has(STANDARD_BREAKOUT_TEMPLATE_ID)) {
        deduped.set(STANDARD_BREAKOUT_TEMPLATE_ID, {
          id: STANDARD_BREAKOUT_TEMPLATE_ID,
          name: 'Standard Lounge',
        });
      }
      breakoutTemplates = [...deduped.values()];
      templateCatalogStatus = 'ready';
      catalogDefaultId = defaultBreakoutTemplateId(breakoutTemplates);
      const validIds = new Set(breakoutTemplates.map((item) => item.id));
      // Before this first (page-cached) load the selects only offered the
      // Standard placeholder, so remapping it to the catalog default can
      // never override a real host choice.
      drafts.applyCatalog(validIds, catalogDefaultId);
      if (isPanelOpen() && view === 'setup') buildSetupView();
    })
    .catch(() => {
      if (!owner?.isCatalogCurrent(operation, catalogGeneration)) return;
      templateCatalogStatus = 'error';
      templateLoadPromise = null;
      showToast('Custom breakout levels are unavailable - using Standard Lounge', {
        type: 'warning',
      });
      if (isPanelOpen() && view === 'setup') buildSetupView();
    });
  return templateLoadPromise;
}

function closePanel() {
  setPanelOpen('breakouts', false);
  if (overlay) overlay.style.display = 'none';
  owner?.stopTick();
}

export function disposeBreakoutPanel() {
  closePanel();
  owner?.dispose();
  owner = null;
  subscriptionsInstalled = false;
  handlers = null;
  overlay?.remove();
  panel = null;
  overlay = null;
  bodyEl = null;
  roomsEl = null;
  timerStatusEl = null;
  closeControlsEl = null;
  broadcastInput = null;
  timerCountdownEl = null;
  closingCountdownEl = null;
  templateLoadPromise = null;
}

export function resetBreakoutPanelDrafts() {
  owner?.resetSession();
  drafts.reset();
  breakoutTemplates = [{ id: STANDARD_BREAKOUT_TEMPLATE_ID, name: 'Standard Lounge' }];
  catalogDefaultId = STANDARD_BREAKOUT_TEMPLATE_ID;
  templateCatalogStatus = 'idle';
  templateLoadPromise = null;
  view = null;
}

function ensurePanel() {
  if (panel) return;
  // Full-screen scrim that dims the world and centers the modal card. Clicking
  // the backdrop (never the card) closes the panel; Esc is intentionally left
  // to the settings menu (hard constraint), so dismissal is backdrop + X only.
  overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(2px)',
    zIndex: String(TOKENS.z.modal),
    pointerEvents: 'auto',
    padding: '16px',
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closePanel();
  });

  panel = document.createElement('div');
  panel.id = 'breakout-panel';
  Object.assign(panel.style, {
    width: '420px',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 140px)',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(18,18,22,0.94)',
    color: TOKENS.text.onDark,
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px',
    boxShadow: TOKENS.shadow.md,
    backdropFilter: 'blur(4px)',
    fontFamily: TOKENS.font,
    fontSize: '13px',
    pointerEvents: 'auto',
    overflow: 'hidden',
  });
  // Clicks inside the card must never reach the backdrop's close handler.
  panel.addEventListener('click', (e) => e.stopPropagation());

  const header = document.createElement('div');
  Object.assign(header.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    flex: 'none',
  });
  const title = document.createElement('div');
  Object.assign(title.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: TOKENS.weight.semibold,
  });
  title.appendChild(createIcon(Users, { size: 16 }));
  const titleText = document.createElement('span');
  titleText.textContent = 'Breakout rooms';
  title.appendChild(titleText);
  const closeBtn = iconButton(X, 'Close');
  closeBtn.onclick = () => closePanel();
  header.append(title, closeBtn);

  bodyEl = document.createElement('div');
  Object.assign(bodyEl.style, {
    flex: '1',
    overflowY: 'auto',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  });
  // Panel inputs must never drive the avatar (WASD keys while typing).
  bodyEl.addEventListener('keydown', (e) => e.stopPropagation());
  bodyEl.addEventListener('keyup', (e) => e.stopPropagation());

  panel.append(header, bodyEl);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
}

function renderForState() {
  const nextView = state && state.active ? 'live' : 'setup';
  if (nextView !== view) {
    view = nextView;
    if (view === 'setup') buildSetupView();
    else buildLiveView();
    return;
  }
  if (view === 'live') repaintLive();
}

// --- Shared small builders ---

function iconButton(icon, titleText) {
  const btn = document.createElement('button');
  btn.title = titleText;
  Object.assign(btn.style, {
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px',
  });
  btn.appendChild(createIcon(icon, { size: 16 }));
  return btn;
}

function actionButton(label, { primary = false, danger = false, icon = null } = {}) {
  const btn = document.createElement('button');
  Object.assign(btn.style, {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    border: 'none',
    borderRadius: TOKENS.radius.md,
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: TOKENS.weight.medium,
    cursor: 'pointer',
    color: '#fff',
    background: danger
      ? 'rgba(244, 67, 54, 0.85)'
      : primary
        ? TOKENS.action.primary.background
        : 'rgba(255,255,255,0.12)',
  });
  if (icon) btn.appendChild(createIcon(icon, { size: 14 }));
  const span = document.createElement('span');
  span.textContent = label;
  btn.appendChild(span);
  return btn;
}

function sectionLabel(text) {
  const el = document.createElement('div');
  el.textContent = text;
  Object.assign(el.style, {
    color: TOKENS.text.muted,
    fontSize: '11px',
    fontWeight: TOKENS.weight.medium,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  });
  return el;
}

function textInput(value, placeholder = '') {
  const input = document.createElement('input');
  input.type = 'text';
  input.value = value;
  input.placeholder = placeholder;
  input.maxLength = BREAKOUT.MAX_NAME_LENGTH;
  Object.assign(input.style, {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: TOKENS.radius.md,
    color: '#fff',
    padding: '6px 10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    width: '100%',
    boxSizing: 'border-box',
  });
  return input;
}

function selectInput(options, value) {
  const select = document.createElement('select');
  Object.assign(select.style, {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: TOKENS.radius.md,
    color: '#fff',
    padding: '4px 6px',
    fontFamily: 'inherit',
    fontSize: '12px',
    cursor: 'pointer',
  });
  for (const opt of options) {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    // Native dropdown lists render on white in most browsers.
    option.style.color = '#000';
    select.appendChild(option);
  }
  select.value = value;
  return select;
}

// Non-manager participants (managers shepherd; they are never assigned).
function assignableRoster() {
  const roster = roomStore.getState().roster.entries;
  return roster.filter((u) => !can(u.role, 'manageBreakouts'));
}

function captureCommand() {
  if (!owner || !handlers) return null;
  return { operation: owner.captureOperation(), handlers };
}

function isCommandCurrent(command) {
  return Boolean(command && handlers === command.handlers && owner?.isCurrent(command.operation));
}

function runCommand(run, onError = null) {
  const command = captureCommand();
  if (!command) return;
  Promise.resolve()
    .then(() => run(command.handlers))
    .catch((error) => {
      if (isCommandCurrent(command)) onError?.(error);
    });
}

// --- Setup view ---

function buildSetupView() {
  bodyEl.replaceChildren();

  // Room count + names.
  const countRow = document.createElement('div');
  Object.assign(countRow.style, { display: 'flex', alignItems: 'center', gap: '10px' });
  countRow.appendChild(sectionLabel('Rooms'));
  const minus = iconButton(Minus, 'Fewer rooms');
  const countEl = document.createElement('span');
  countEl.textContent = String(drafts.count);
  Object.assign(countEl.style, {
    fontWeight: TOKENS.weight.semibold,
    minWidth: '18px',
    textAlign: 'center',
  });
  const plus = iconButton(Plus, 'More rooms');
  for (const b of [minus, plus]) b.style.background = 'rgba(255,255,255,0.12)';
  minus.style.borderRadius = plus.style.borderRadius = TOKENS.radius.md;
  countRow.append(minus, countEl, plus);

  const namesWrap = document.createElement('div');
  Object.assign(namesWrap.style, { display: 'flex', flexDirection: 'column', gap: '6px' });
  const templateStatus = document.createElement('div');
  Object.assign(templateStatus.style, { color: TOKENS.text.muted, fontSize: '11px' });
  if (templateCatalogStatus === 'loading')
    templateStatus.textContent = 'Loading breakout levels...';
  else if (templateCatalogStatus === 'error') {
    templateStatus.textContent = 'Custom levels unavailable - Standard Lounge will be used.';
  }

  const syncNames = () => {
    countEl.textContent = String(drafts.count);
    // Preserve typed names; extend/trim to the count.
    drafts.resize(drafts.count, catalogDefaultId);
    namesWrap.replaceChildren(
      ...drafts.names.map((name, i) => {
        const row = document.createElement('div');
        Object.assign(row.style, {
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(130px, 0.8fr)',
          gap: '6px',
        });
        const input = textInput(name, `Room ${i + 1}`);
        input.setAttribute('aria-label', `Room ${i + 1} name`);
        input.addEventListener('input', () => {
          drafts.names[i] = input.value;
        });
        const select = selectInput(
          breakoutTemplates.map((template) => ({ value: template.id, label: template.name })),
          drafts.templateIds[i],
        );
        select.setAttribute('aria-label', `Room ${i + 1} level`);
        select.style.width = '100%';
        select.addEventListener('change', () => {
          drafts.templateIds[i] = select.value;
        });
        row.append(input, select);
        return row;
      }),
    );
    if (drafts.mode === 'assign') syncAssignGrid();
  };
  minus.onclick = () => {
    if (drafts.count > 1) {
      drafts.count -= 1;
      syncNames();
    }
  };
  plus.onclick = () => {
    if (drafts.count < BREAKOUT.MAX_ROOMS) {
      drafts.count += 1;
      syncNames();
    }
  };

  // Mode picker.
  const modeWrap = document.createElement('div');
  Object.assign(modeWrap.style, { display: 'flex', flexDirection: 'column', gap: '6px' });
  modeWrap.appendChild(sectionLabel('How people get to rooms'));
  const modes = [
    { value: 'assign', label: 'Assign manually', hint: 'You place each person below' },
    { value: 'auto', label: 'Split evenly', hint: 'Random even split when rooms open' },
    {
      value: 'choose',
      label: 'Let people choose',
      hint: 'Guests pick a room via portals or the join list',
    },
  ];
  const modeCards = modes.map((m) => {
    const card = document.createElement('label');
    Object.assign(card.style, {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      padding: '8px 10px',
      borderRadius: TOKENS.radius.md,
      border: '1px solid rgba(255,255,255,0.14)',
      cursor: 'pointer',
    });
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'breakout-mode';
    radio.value = m.value;
    radio.checked = drafts.mode === m.value;
    radio.style.marginTop = '2px';
    const text = document.createElement('div');
    const label = document.createElement('div');
    label.textContent = m.label;
    label.style.fontWeight = TOKENS.weight.medium;
    const hint = document.createElement('div');
    hint.textContent = m.hint;
    Object.assign(hint.style, { color: TOKENS.text.muted, fontSize: '12px' });
    text.append(label, hint);
    card.append(radio, text);
    radio.addEventListener('change', () => {
      if (radio.checked) {
        drafts.mode = m.value;
        paintModeCards();
        assignSection.style.display = drafts.mode === 'assign' ? 'flex' : 'none';
        autoHint.style.display = drafts.mode === 'auto' ? 'block' : 'none';
        if (drafts.mode === 'assign') syncAssignGrid();
      }
    });
    return { card, value: m.value };
  });
  const paintModeCards = () => {
    for (const { card, value } of modeCards) {
      card.style.borderColor =
        value === drafts.mode ? 'rgba(25, 118, 210, 0.9)' : 'rgba(255,255,255,0.14)';
      card.style.background = value === drafts.mode ? 'rgba(25, 118, 210, 0.12)' : 'none';
    }
  };
  paintModeCards();
  modeWrap.append(...modeCards.map((m) => m.card));

  // Timer.
  const timerRow = document.createElement('div');
  Object.assign(timerRow.style, { display: 'flex', alignItems: 'center', gap: '10px' });
  timerRow.appendChild(sectionLabel('Timer'));
  const timerSelect = selectInput(
    [
      { value: '0', label: 'No timer' },
      { value: '5', label: '5 minutes' },
      { value: '10', label: '10 minutes' },
      { value: '15', label: '15 minutes' },
      { value: '30', label: '30 minutes' },
    ],
    String(drafts.minutes),
  );
  timerSelect.addEventListener('change', () => {
    drafts.minutes = Number(timerSelect.value);
  });
  timerRow.appendChild(timerSelect);

  // Manual-assignment grid (assign mode only).
  const assignSection = document.createElement('div');
  Object.assign(assignSection.style, {
    display: drafts.mode === 'assign' ? 'flex' : 'none',
    flexDirection: 'column',
    gap: '6px',
  });
  assignSection.appendChild(sectionLabel('Who goes where'));
  const assignGrid = document.createElement('div');
  Object.assign(assignGrid.style, { display: 'flex', flexDirection: 'column', gap: '4px' });
  assignSection.appendChild(assignGrid);

  const syncAssignGrid = () => {
    const roster = assignableRoster();
    assignGrid.replaceChildren();
    if (!roster.length) {
      const empty = document.createElement('div');
      empty.textContent = 'No one else is here yet - you can assign people after opening too.';
      Object.assign(empty.style, { color: TOKENS.text.muted, fontSize: '12px' });
      assignGrid.appendChild(empty);
      return;
    }
    for (const user of roster) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
      });
      const name = document.createElement('span');
      name.textContent = user.name;
      Object.assign(name.style, {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      });
      const options = [{ value: '', label: 'Main hall' }].concat(
        drafts.names.slice(0, drafts.count).map((n, i) => ({
          value: `r${i + 1}`,
          label: (n || '').trim() || `Room ${i + 1}`,
        })),
      );
      const key = String(user.actorNr);
      const select = selectInput(options, drafts.assignments[key] ?? '');
      select.addEventListener('change', () => {
        if (select.value) drafts.assignments[key] = select.value;
        else delete drafts.assignments[key];
      });
      row.append(name, select);
      assignGrid.appendChild(row);
    }
  };

  // Auto-split hint.
  const autoHint = document.createElement('div');
  Object.assign(autoHint.style, {
    display: drafts.mode === 'auto' ? 'block' : 'none',
    color: TOKENS.text.muted,
    fontSize: '12px',
  });

  // Open button.
  const openBtn = actionButton('Open rooms', { primary: true, icon: DoorOpen });
  openBtn.style.alignSelf = 'stretch';
  openBtn.style.justifyContent = 'center';
  openBtn.onclick = async () => {
    const command = captureCommand();
    if (!command) return;
    const request = {
      mode: drafts.mode,
      rooms: drafts.names.slice(0, drafts.count).map((name, i) => {
        const templateId = drafts.templateIds[i] || catalogDefaultId;
        const template =
          breakoutTemplates.find((item) => item.id === templateId) || breakoutTemplates[0];
        return { name, templateId: template.id, templateName: template.name };
      }),
      minutes: drafts.minutes || null,
      assignments: drafts.mode === 'assign' ? { ...drafts.assignments } : {},
    };
    openBtn.disabled = true;
    try {
      await command.handlers.onOpen(request);
      if (isCommandCurrent(command)) showToast('Breakout rooms are open', { type: 'success' });
      // The room-state publication flips the view to live.
    } catch (err) {
      if (isCommandCurrent(command)) {
        showToast(`Could not open rooms: ${err.message}`, { type: 'error' });
      }
    } finally {
      if (isCommandCurrent(command)) openBtn.disabled = false;
    }
  };

  bodyEl.append(
    countRow,
    namesWrap,
    ...(templateStatus.textContent ? [templateStatus] : []),
    modeWrap,
    timerRow,
    assignSection,
    autoHint,
    openBtn,
  );
  syncNames();
  const count = assignableRoster().length;
  autoHint.textContent = count
    ? `${count} ${count === 1 ? 'person' : 'people'} will be split evenly when rooms open.`
    : 'People will be split evenly as they arrive after you open.';
}

// --- Live view ---

let roomsEl = null;
let timerStatusEl = null;
let closeControlsEl = null;
let broadcastInput = null;
// Countdown text nodes updated in place each tick (rebuilding the whole
// section every second would tear down buttons/selects mid-interaction -
// same discipline as the People panel's refreshTick).
let timerCountdownEl = null;
let closingCountdownEl = null;
let lastRoomsSignature = null;
let lastControlsSignature = null;

// Narrow the state union for the live view - tsc can't carry the
// `state.active` guard across function boundaries.
function liveState() {
  return state && state.active
    ? /** @type {import('../../utils/breakoutProtocol').BreakoutState} */ (state)
    : null;
}

function liveSignatures(s) {
  const roster = assignableRoster();
  return breakoutLiveSignatures(s.rev, s.endsAt, s.closingAt, roster);
}

function buildLiveView() {
  bodyEl.replaceChildren();
  lastRoomsSignature = null;
  lastControlsSignature = null;

  roomsEl = document.createElement('div');
  Object.assign(roomsEl.style, { display: 'flex', flexDirection: 'column', gap: '10px' });

  // Broadcast composer.
  const broadcastWrap = document.createElement('div');
  Object.assign(broadcastWrap.style, { display: 'flex', flexDirection: 'column', gap: '6px' });
  broadcastWrap.appendChild(sectionLabel('Announce to everyone'));
  const composerRow = document.createElement('div');
  Object.assign(composerRow.style, { display: 'flex', gap: '6px' });
  broadcastInput = textInput('', 'e.g. Wrapping up in 5 minutes');
  broadcastInput.maxLength = CONFIG.CHAT.MAX_MESSAGE_LENGTH;
  const sendBtn = actionButton('Send', { primary: true, icon: Megaphone });
  const sendBroadcast = async () => {
    const text = broadcastInput.value.trim();
    if (!text) return;
    const command = captureCommand();
    if (!command) return;
    sendBtn.disabled = true;
    try {
      await command.handlers.onBroadcast(text);
      if (isCommandCurrent(command)) {
        broadcastInput.value = '';
        showToast('Announcement sent to everyone', { type: 'success' });
      }
    } catch (err) {
      if (isCommandCurrent(command)) {
        showToast(`Announcement failed: ${err.message}`, { type: 'error' });
      }
    } finally {
      if (isCommandCurrent(command)) sendBtn.disabled = false;
    }
  };
  sendBtn.onclick = sendBroadcast;
  broadcastInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') sendBroadcast();
  });
  composerRow.append(broadcastInput, sendBtn);
  broadcastWrap.appendChild(composerRow);

  // Timer + close controls.
  timerStatusEl = document.createElement('div');
  Object.assign(timerStatusEl.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  });
  closeControlsEl = document.createElement('div');
  Object.assign(closeControlsEl.style, { display: 'flex', flexDirection: 'column', gap: '6px' });

  bodyEl.append(roomsEl, broadcastWrap, timerStatusEl, closeControlsEl);
  repaintLive();
}

// Repaint the live sections. Structure rebuilds only when its signature
// changes (a select being browsed must never be torn down mid-click);
// countdown text updates in place every tick. Never rebuild while one of our
// inputs/selects holds focus (rename, a move dropdown) - defer instead.
function repaintLive() {
  const s = liveState();
  if (!isPanelOpen() || view !== 'live' || !s) return;
  const active = document.activeElement;
  const focusInPanel =
    panel.contains(active) &&
    /^(INPUT|SELECT|TEXTAREA)$/.test(active?.tagName || '') &&
    active !== broadcastInput;
  const { roomsSig, controlsSig } = liveSignatures(s);
  if (!focusInPanel && roomsSig !== lastRoomsSignature) {
    paintRooms(s);
    lastRoomsSignature = roomsSig;
  }
  if (!focusInPanel && controlsSig !== lastControlsSignature) {
    paintTimerAndClose(s);
    lastControlsSignature = controlsSig;
  }
  // No flag needed to remember a deferred repaint: the skipped branches leave
  // lastRoomsSignature/lastControlsSignature stale on purpose, so the next
  // onTick (1s) repaints as soon as focus leaves the input.
  updateCountdownTexts(s);
}

function updateCountdownTexts(s) {
  const now = Date.now();
  if (timerCountdownEl && s.endsAt) {
    timerCountdownEl.textContent = `Time left: ${formatCountdown(s.endsAt - now)}`;
  }
  if (closingCountdownEl && s.closingAt) {
    closingCountdownEl.textContent = `Closing - everyone returns in ${formatCountdown(s.closingAt - now)}`;
  }
}

/** @param {import('../../utils/breakoutProtocol').BreakoutState} s */
function paintRooms(s) {
  const roster = assignableRoster();
  roomsEl.replaceChildren();

  const moveOptions = [{ value: '', label: 'Main hall' }].concat(
    s.rooms.map((r) => ({ value: r.id, label: r.name })),
  );

  const personRow = (user, currentRoomId) => {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      paddingLeft: '2px',
    });
    const name = document.createElement('span');
    name.textContent = user.name;
    Object.assign(name.style, {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: '12px',
    });
    const select = selectInput(moveOptions, currentRoomId || '');
    select.title = `Move ${user.name}`;
    select.addEventListener('change', async () => {
      const command = captureCommand();
      if (!command) return;
      const roomId = select.value || null;
      select.disabled = true;
      try {
        await command.handlers.onAssign(user.actorNr, roomId);
      } catch (err) {
        if (isCommandCurrent(command)) showToast(`Move failed: ${err.message}`, { type: 'error' });
      } finally {
        if (isCommandCurrent(command)) select.disabled = false;
      }
    });
    row.append(name, select);
    return row;
  };

  const roomCard = (titleText, people, room) => {
    const card = document.createElement('div');
    Object.assign(card.style, {
      border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: TOKENS.radius.md,
      padding: '8px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    });
    const head = document.createElement('div');
    Object.assign(head.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
    });
    const title = document.createElement('span');
    title.style.fontWeight = TOKENS.weight.semibold;
    title.textContent = `${titleText} (${people.length})`;
    head.appendChild(title);

    if (room) {
      const titleWrap = document.createElement('span');
      Object.assign(titleWrap.style, {
        display: 'flex',
        flexDirection: 'column',
        minWidth: '0',
      });
      const templateLabel = document.createElement('span');
      templateLabel.textContent = room.templateName;
      Object.assign(templateLabel.style, {
        color: TOKENS.text.muted,
        fontSize: '11px',
        fontWeight: TOKENS.weight.regular,
      });
      head.replaceChild(titleWrap, title);
      titleWrap.append(title, templateLabel);
      const buttons = document.createElement('span');
      Object.assign(buttons.style, { display: 'inline-flex', gap: '6px' });
      // Click-to-rename: swap the title for an input; commit on Enter/blur.
      title.style.cursor = 'pointer';
      title.title = 'Click to rename';
      title.onclick = () => {
        const input = textInput(room.name);
        Object.assign(input.style, { fontSize: '13px', padding: '2px 6px', width: '150px' });
        titleWrap.replaceChild(input, title);
        input.focus();
        input.select();
        const commit = async () => {
          const value = input.value.trim();
          titleWrap.replaceChild(title, input);
          if (value && value !== room.name) {
            const command = captureCommand();
            if (!command) return;
            try {
              await command.handlers.onRename(room.id, value);
            } catch (err) {
              if (isCommandCurrent(command)) {
                showToast(`Rename failed: ${err.message}`, { type: 'error' });
              }
            }
          }
          if (owner) repaintLive();
        };
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') input.blur();
          else if (e.key === 'Escape') {
            input.value = room.name;
            input.blur();
          }
        });
        input.addEventListener('blur', commit);
      };
      const visitBtn = actionButton('Visit', { icon: Footprints });
      visitBtn.style.padding = '3px 8px';
      visitBtn.style.fontSize = '12px';
      visitBtn.onclick = async () => {
        const command = captureCommand();
        if (!command) return;
        visitBtn.disabled = true;
        try {
          await command.handlers.onVisit(room.id);
        } finally {
          if (isCommandCurrent(command)) visitBtn.disabled = false;
        }
      };
      buttons.appendChild(visitBtn);
      head.appendChild(buttons);
    }

    if (people.length) {
      card.append(head, ...people.map((u) => personRow(u, room ? room.id : null)));
    } else {
      const empty = document.createElement('div');
      empty.textContent = 'Empty';
      Object.assign(empty.style, { color: TOKENS.text.muted, fontSize: '12px' });
      card.append(head, empty);
    }
    return card;
  };

  for (const room of s.rooms) {
    const occupants = roster.filter((u) => u.breakoutRoomId === room.id);
    roomsEl.appendChild(roomCard(room.name, occupants, room));
  }
  const inMain = roster.filter((u) => !u.breakoutRoomId);
  roomsEl.appendChild(roomCard('In main hall', inMain, null));
}

/** @param {import('../../utils/breakoutProtocol').BreakoutState} s */
function paintTimerAndClose(s) {
  timerStatusEl.replaceChildren();
  closeControlsEl.replaceChildren();
  timerCountdownEl = null;
  closingCountdownEl = null;
  const now = Date.now();

  // Timer row.
  const timerLabel = document.createElement('span');
  Object.assign(timerLabel.style, { color: TOKENS.text.muted, fontSize: '12px' });
  if (s.endsAt) {
    timerLabel.textContent = `Time left: ${formatCountdown(s.endsAt - now)}`;
    timerCountdownEl = timerLabel;
    const extendBtn = actionButton('+5 min', { icon: TimerReset });
    extendBtn.style.padding = '3px 8px';
    extendBtn.style.fontSize = '12px';
    extendBtn.onclick = () => runCommand((current) => current.onExtendTimer(5));
    const offBtn = actionButton('Cancel timer');
    offBtn.style.padding = '3px 8px';
    offBtn.style.fontSize = '12px';
    offBtn.onclick = () => runCommand((current) => current.onSetTimer(null));
    timerStatusEl.append(timerLabel, extendBtn, offBtn);
  } else {
    timerLabel.textContent = 'No timer';
    const startBtn = actionButton('Start 10 min timer', { icon: TimerReset });
    startBtn.style.padding = '3px 8px';
    startBtn.style.fontSize = '12px';
    startBtn.onclick = () => runCommand((current) => current.onSetTimer(10));
    timerStatusEl.append(timerLabel, startBtn);
  }

  // Close controls.
  if (s.closingAt) {
    const closing = document.createElement('div');
    closing.textContent = `Closing - everyone returns in ${formatCountdown(s.closingAt - now)}`;
    Object.assign(closing.style, { color: '#ffb74d', fontWeight: TOKENS.weight.medium });
    closingCountdownEl = closing;
    const cancelBtn = actionButton('Cancel closing');
    cancelBtn.onclick = () =>
      runCommand(
        (current) => current.onCloseCancel(),
        (err) => {
          showToast(`Cancel failed: ${err.message}`, { type: 'error' });
        },
      );
    closeControlsEl.append(closing, cancelBtn);
  } else {
    const closeBtn = actionButton('Close rooms', { danger: true, icon: DoorOpen });
    closeBtn.style.justifyContent = 'center';
    closeBtn.onclick = () => {
      modalManager.confirm({
        title: 'Close breakout rooms',
        message: `Everyone returns to the main hall after a ${Math.round(BREAKOUT.GRACE_MS / 1000)}-second countdown. You can cancel during the countdown.`,
        confirmText: 'Close rooms',
        confirmType: 'danger',
        theme: 'dark',
        onConfirm: () => {
          runCommand(
            (current) => current.onCloseStart(),
            (err) => {
              showToast(`Close failed: ${err.message}`, { type: 'error' });
            },
          );
        },
      });
    };
    closeControlsEl.append(closeBtn);
  }
}

// 1s tick while open: countdown text, occupancy signature checks, and any
// repaint deferred because an input/select held focus.
function onTick() {
  if (!isPanelOpen()) return;
  if (view === 'live') repaintLive();
}
